// Service for external recording operations
import {
  uploadAudioFile,
  updateDatabaseEntry,
  createBackup,
  getUserSchema,
  sanitizeRollingSessionSlug,
} from "../utils/databaseUtils";
import {
  supabasePublic,
  getSupabase,
} from "../../../components/supabaseClient";
import {
  backupBucketName,
  DEFAULT_AUDIO_PERIODIC_BACKUP,
} from "../../../components/env";
import { isAudioUrlPlayable, getFileExtension } from "../utils/fileUtils";
import { tableForPatientId } from "../../medstudent/chartSource";
import { recorderStateRow } from "../../../utils/recorderSchema";

/** Parse public.schema_settings.audio_periodic_backup (JSONB). */
export function parseAudioPeriodicBackupSettings(raw) {
  let o = raw;
  if (o == null) {
    return { ...DEFAULT_AUDIO_PERIODIC_BACKUP };
  }
  if (typeof o === "string") {
    try {
      o = JSON.parse(o);
    } catch {
      return { ...DEFAULT_AUDIO_PERIODIC_BACKUP };
    }
  }
  if (typeof o !== "object" || o === null || Array.isArray(o)) {
    return { ...DEFAULT_AUDIO_PERIODIC_BACKUP };
  }
  const kc = Number(o.keep_count);
  const sec = Number(o.interval_seconds);
  const keepCount = Number.isFinite(kc)
    ? Math.max(0, Math.min(100, Math.trunc(kc)))
    : DEFAULT_AUDIO_PERIODIC_BACKUP.keepCount;
  const intervalSeconds = Number.isFinite(sec)
    ? Math.max(5, Math.min(600, Math.trunc(sec)))
    : DEFAULT_AUDIO_PERIODIC_BACKUP.intervalSeconds;
  return {
    keepCount,
    intervalSeconds,
    intervalMs: intervalSeconds * 1000,
  };
}

async function fetchAudioPeriodicBackupSettingsFromDb(rawSchema) {
  try {
    const client = await supabasePublic();
    let schema = (rawSchema || "public").trim();
    if (schema.includes(",")) schema = schema.split(",")[0].trim();
    const { data, error } = await client
      .from("schema_settings")
      .select("audio_periodic_backup")
      .eq("schema_name", schema)
      .maybeSingle();
    if (error) throw error;
    return parseAudioPeriodicBackupSettings(data?.audio_periodic_backup);
  } catch (e) {
    console.warn(
      "[audio-backup] audio_periodic_backup unavailable, using default:",
      e?.message || e,
    );
    return { ...DEFAULT_AUDIO_PERIODIC_BACKUP };
  }
}

function normalizeUrlNoQuery(url) {
  if (!url) return "";
  return url.split("?")[0];
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Remove legacy `backup_{id}_{N}` refs and sibling periodic keys except `keepKey` (same patientId). */
function purgeStalePeriodicBackupLocalRefs(patientId, keepKey) {
  if (patientId == null || typeof Storage === "undefined") return;
  const id = String(patientId);
  const prefix = `backup_${id}_`;
  const legacyNumeric = new RegExp(`^${escapeRegex(prefix)}\\d+$`);
  const periodicPrefix = `${prefix}periodic_`;
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(prefix)) continue;
    if (key === keepKey) continue;
    if (legacyNumeric.test(key) || key.startsWith(periodicPrefix)) {
      toRemove.push(key);
    }
  }
  for (const k of toRemove) {
    try {
      localStorage.removeItem(k);
      console.log("🧹 [PERIODIC-BACKUP] Removed stale backup ref:", k);
    } catch {
      // ignore quota / privacy mode
    }
  }
}

function backupStoragePathFromPublicUrl(url, bucket) {
  if (!url || !bucket) return null;
  const clean = normalizeUrlNoQuery(url);
  const markers = [`/object/public/${bucket}/`];
  try {
    const enc = encodeURIComponent(bucket);
    if (enc !== bucket) markers.push(`/object/public/${enc}/`);
  } catch {
    // ignore
  }
  let rel = "";
  for (const m of markers) {
    const idx = clean.indexOf(m);
    if (idx >= 0) {
      rel = clean.slice(idx + m.length);
      break;
    }
  }
  if (!rel) return null;
  let path = rel;
  for (let i = 0; i < 4; i++) {
    try {
      const dec = decodeURIComponent(path.replace(/\+/g, "%20"));
      if (dec === path) break;
      path = dec;
    } catch {
      break;
    }
  }
  path = path.replace(/^\/+/, "");
  return path || null;
}

// createBackup writes a `<basename>_metadata.json` sidecar next to each audio
// upload. When we remove an audio backup we have to remove its sidecar too,
// otherwise the bucket fills with orphan JSON pointing at deleted files.
function metadataPathForAudioPath(audioPath) {
  if (!audioPath) return null;
  return (
    audioPath.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "") + "_metadata.json"
  );
}

function expandPathsWithMetadata(paths) {
  if (!Array.isArray(paths) || paths.length === 0) return [];
  const expanded = new Set();
  for (const p of paths) {
    if (!p) continue;
    expanded.add(p);
    const meta = metadataPathForAudioPath(p);
    if (meta && meta !== p) expanded.add(meta);
  }
  return [...expanded];
}

/**
 * Decide which backup storage paths to remove. Keeps a newest-first prefix until
 * `maxKeep` eligible files are included (default via schema_settings keep_count).
 * When `trustUploadedRefs` is true, entries are treated as playable if they have a
 * public URL (avoids fetching/decoding blobs during recording — fetches often fail
 * and previously caused "keep everything" retention).
 */
async function selectBackupPathsToRemove(
  backupEntries,
  maxKeep,
  preservedRecordingUrl,
  bucket,
  options = {},
) {
  const { trustUploadedRefs = false } = options;
  const sorted = [...backupEntries].sort((a, b) => {
    const nb = (b.backupNumber || 0) - (a.backupNumber || 0);
    if (nb !== 0) return nb;
    const ta = Date.parse(a.timestamp || "") || 0;
    const tb = Date.parse(b.timestamp || "") || 0;
    return tb - ta;
  });
  if (sorted.length === 0) return [];

  const annotated = await Promise.all(
    sorted.map(async (b) => {
      const objectPath =
        b.path || backupStoragePathFromPublicUrl(b.url, bucket) || null;
      const playable = trustUploadedRefs
        ? !!normalizeUrlNoQuery(b.url)
        : await isAudioUrlPlayable(b.url);
      return { ...b, objectPath, playable };
    }),
  );

  const norm = normalizeUrlNoQuery;
  const preservedNorm = norm(preservedRecordingUrl);
  const rowKey = (b) => b.objectPath || norm(b.url);
  const totalPlayable = annotated.filter((b) => b.playable).length;
  const keptKeys = new Set();

  if (maxKeep <= 0) {
    if (preservedNorm) {
      for (const b of annotated) {
        if (norm(b.url) === preservedNorm) keptKeys.add(rowKey(b));
      }
    }
  } else if (totalPlayable < maxKeep) {
    annotated.forEach((b) => keptKeys.add(rowKey(b)));
  } else {
    let playableInPrefix = 0;
    for (const b of annotated) {
      keptKeys.add(rowKey(b));
      if (b.playable) playableInPrefix++;
      if (playableInPrefix >= maxKeep) break;
    }
  }

  if (preservedNorm) {
    for (const b of annotated) {
      if (norm(b.url) === preservedNorm) keptKeys.add(rowKey(b));
    }
  }

  const removePaths = [];
  for (const b of annotated) {
    if (!b.objectPath) continue;
    if (!keptKeys.has(rowKey(b))) removePaths.push(b.objectPath);
  }
  return [...new Set(removePaths)];
}

/**
 * While recording: drop older periodic audio backups from storage so only the latest
 * `keep_count` remain (default 1). Removes matching localStorage refs for deleted objects only.
 */
async function prunePeriodicAudioBackupsDuringRecording(
  patientId,
  preservedRecordingUrl = null,
) {
  if (!patientId) {
    return { success: false, error: "No patientId" };
  }

  const bucket = backupBucketName || "backup-audio";
  let userSchema = "public";
  try {
    userSchema = await getUserSchema();
  } catch {
    // fall back
  }

  const { keepCount: maxKeep } =
    await fetchAudioPeriodicBackupSettingsFromDb(userSchema);

  const refs = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(`backup_${patientId}_`)) {
      try {
        const raw = localStorage.getItem(key);
        const data = raw ? JSON.parse(raw) : null;
        if (data && data.url) refs.push({ key, data });
      } catch {
        // ignore
      }
    }
  }

  if (refs.length === 0) {
    return { success: true, pruned: 0 };
  }

  const pathsToRemove = await selectBackupPathsToRemove(
    refs.map((r) => r.data),
    maxKeep,
    preservedRecordingUrl,
    bucket,
    { trustUploadedRefs: true },
  );
  const removeAudioPaths = new Set(pathsToRemove);
  const allPathsToRemove = expandPathsWithMetadata(pathsToRemove);

  if (allPathsToRemove.length > 0) {
    try {
      const supabase = await getSupabase();
      const { error: removeError } = await supabase.storage
        .from(bucket)
        .remove(allPathsToRemove);
      if (removeError) {
        console.warn(
          "🧹 [PERIODIC-BACKUP] Storage prune warning:",
          removeError.message,
        );
      } else {
        console.log(
          "🧹 [PERIODIC-BACKUP] Removed older backup object(s):",
          allPathsToRemove.length,
        );
      }
    } catch (e) {
      console.warn(
        "🧹 [PERIODIC-BACKUP] Storage prune failed (non-critical):",
        e?.message || e,
      );
    }
  }

  for (const { key, data } of refs) {
    const objectPath =
      data.path || backupStoragePathFromPublicUrl(data.url, bucket);
    if (objectPath && removeAudioPaths.has(objectPath)) {
      try {
        localStorage.removeItem(key);
        console.log("🧹 [PERIODIC-BACKUP] Cleared stale backup ref:", key);
      } catch (e) {
        console.warn(
          "🧹 [PERIODIC-BACKUP] Failed to remove local ref:",
          key,
          e?.message || e,
        );
      }
    }
  }

  return {
    success: true,
    pruned: pathsToRemove.length,
    maxKeep,
  };
}

export const ExternalRecordingService = {
  // Session tracking for deduplication
  processedSessions: new Set(),
  sessionTimeoutMs: 5 * 60 * 1000, // 5 minutes

  /**
   * Check if session has already been processed
   */
  isDuplicateSession: function (sessionId) {
    if (!sessionId) return false;

    // Clean up old sessions first
    this.cleanupProcessedSessions();

    return this.processedSessions.has(sessionId);
  },

  /**
   * Mark session as processed
   */
  markSessionAsProcessed: function (sessionId) {
    if (!sessionId) return;

    this.processedSessions.add(sessionId);

    // Auto-cleanup after timeout
    setTimeout(() => {
      this.processedSessions.delete(sessionId);
    }, this.sessionTimeoutMs);
  },

  /**
   * Clean up old processed sessions
   */
  cleanupProcessedSessions: function () {
    // This is a simplified cleanup - in production you might want a more sophisticated approach
    if (this.processedSessions.size > 100) {
      const sessionsArray = Array.from(this.processedSessions);
      // Keep only the last 50 sessions
      sessionsArray.slice(0, 50).forEach((session) => {
        this.processedSessions.delete(session);
      });
    }
  },

  /**
   * Create standardized filename for audio recording
   */
  createStandardizedFilename: (data) => {
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, "-");
    const ext = getFileExtension(
      data?.audioBlob?.type || "audio/webm",
    );
    const filename = `recording_${timestamp}.${ext}`;

    console.log("📁 [SIMPLIFIED-FILENAME] Generated filename:", filename);
    return filename;
  },

  /**
   * Process completed audio recording from external window
   */
  processCompletedRecording: async (data, config) => {
    const { onAudioSaved, bucketName, tableName, supabaseUrl, state } = config;
    // Schema the chart row lives in. The popup reports the one it actually used
    // (`storageSchemaName`); the caller's row tag is the fallback. Without it
    // every write below runs in the VIEWER's schema, which for a cross-schema
    // chart holds no such row.
    const chartRowSchema =
      data?.storageSchemaName || recorderStateRow(state)?._schema || null;

    console.log("🎤 [PARENT-WINDOW] Received message event:", {
      type: data?.type,
      hasAudioBlob: !!data?.audioBlob,
      patientId: data?.patientId,
      source: "external-window",
      isBackup: data?.isBackup,
      sessionId: data?.sessionId,
    });

    // Handle periodic backups differently - don't save them permanently
    if (data.type === "AUDIO_RECORDING_BACKUP") {
      console.log(
        "💾 [PARENT-WINDOW] Processing periodic backup (temporary only)",
      );
      return await ExternalRecordingService.processPeriodicBackup(data, config);
    }

    if (data.type !== "AUDIO_RECORDING_COMPLETE") {
      console.log("🎤 [PARENT-WINDOW] Ignored message type:", data?.type);
      return { success: false, error: "Invalid message type" };
    }

    // Prevent duplicate processing using session tracking
    if (
      data.sessionId &&
      ExternalRecordingService.isDuplicateSession(data.sessionId)
    ) {
      console.log(
        "🚫 [PARENT-WINDOW] Duplicate session detected, ignoring:",
        data.sessionId,
      );
      return { success: false, error: "Duplicate session - already processed" };
    }

    // Mark session as processed
    if (data.sessionId) {
      ExternalRecordingService.markSessionAsProcessed(data.sessionId);
    }

    console.log(
      "🎤 [PARENT-WINDOW] ✅ CONFIRMED: Received FINAL audio recording from external window",
    );
    console.log("🎤 [PARENT-WINDOW] Message data:", {
      patientId: data.patientId,
      patientName: data.patientName,
      audioBlobSize: data.audioBlob?.size,
      audioBlobType: data.audioBlob?.type,
      duration: data.duration,
      timestamp: data.timestamp,
      totalBackups: data.totalBackups,
      liveTranscriptionEnabled: data.liveTranscription,
      liveTranscriptTextLength: data.liveTranscriptText?.length ?? 0,
      liveTranscriptPreview: data.liveTranscriptText
        ? data.liveTranscriptText.substring(0, 80)
        : "(empty)",
    });

    // Check if external recorder saved the audio - if not, automatically use backup
    const hasAudioFromExternalRecorder =
      data.audioBlob && data.audioBlob.size > 0;

    if (!hasAudioFromExternalRecorder) {
      console.warn(
        "⚠️ [PARENT-WINDOW] No audio from external recorder - checking for backup...",
      );

      // Try to get the last successful backup
      const lastBackup = ExternalRecordingService.getLastBackupForPatient(
        data.patientId,
      );

      if (lastBackup) {
        console.log(
          "💾 [PARENT-WINDOW] Found backup - automatically using backup audio:",
          lastBackup,
        );

        try {
          // Use the backup audio data for database entry update
          const fallbackAudioData = {
            name: `Recording ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} (backup)`,
            description: "External recording - restored from backup",
            url: lastBackup.url,
            duration: data.duration || lastBackup.duration || 0,
            source: "backup_restored",
            timestamp: data.timestamp || lastBackup.timestamp,
            uploaded_at: new Date().toISOString(),
            verified: true,
            type: "audio",
            patient_id: data.patientId,
            recording_date: new Date().toISOString(),
            displayName: `Recording ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} (backup)`,
            isFromBackup: true,
            backupNumber: lastBackup.backupNumber,
          };

          // Update database with backup audio reference
          const updateResult = await updateDatabaseEntry(
            tableForPatientId(data.patientId),
            data.patientId,
            lastBackup.url,
            data.duration || lastBackup.duration || 0,
            {
              audio_source: "backup_restored",
              recording_timestamp: data.timestamp || lastBackup.timestamp,
              verified_update: true,
              is_backup_restored: true,
              total_backups: data.totalBackups || 0,
            },
            chartRowSchema,
          );

          if (!updateResult.success) {
            throw new Error(
              `Database update verification failed: ${
                updateResult.error || "Unknown error"
              }`,
            );
          }

          console.log("✅ [PARENT-WINDOW] Database updated with backup audio");

          // Clean up temporary backups after using backup
          try {
            let userSchemaForRetention;
            try {
              userSchemaForRetention = await getUserSchema();
            } catch {
              userSchemaForRetention = "public";
            }
            const cleanupResult =
              await ExternalRecordingService.cleanupTemporaryBackups(
                data.patientId,
                lastBackup.url,
                { userSchema: userSchemaForRetention },
              );
            ExternalRecordingService.clearRecoveryBackup(data.patientId);

            if (cleanupResult.success) {
              console.log(
                "🧹 [PARENT-WINDOW] Cleanup completed after backup restore:",
                cleanupResult.message,
              );
            }
          } catch (cleanupError) {
            console.warn(
              "⚠️ [PARENT-WINDOW] Cleanup warning (non-critical):",
              cleanupError.message,
            );
          }

          // Call the onAudioSaved callback with the backup data
          if (onAudioSaved) {
            onAudioSaved({
              success: true,
              audioUrl: lastBackup.url,
              audioData: fallbackAudioData,
              verification: updateResult.verification,
              liveTranscriptText: data.liveTranscriptText || "",
              generateAiRecap: data.generateAiRecap || false,
              duration: data.duration || lastBackup.duration || 0,
              timestamp: data.timestamp,
              sessionId: data.sessionId || null,
              patientId: data.patientId || null,
              isFromBackup: true,
            });
          }

          ExternalRecordingService.triggerRefresh(
            data.patientId,
            lastBackup.url,
          );

          console.log(
            "🎉 [PARENT-WINDOW] Complete backup restore flow successful for patient ID:",
            data.patientId,
          );

          return {
            success: true,
            audioUrl: lastBackup.url,
            audioData: fallbackAudioData,
            verification: updateResult.verification,
            isFromBackup: true,
          };
        } catch (backupError) {
          console.error(
            "❌ [PARENT-WINDOW] Error restoring from backup:",
            backupError,
          );
          // If backup restore also fails, continue to regular error handling below
        }
      } else {
        console.warn(
          "⚠️ [PARENT-WINDOW] No backup available and external recorder has no audio",
        );
      }
    }

    try {
      console.log("🎤 [PARENT-WINDOW] Starting FINAL audio processing...");

      console.log(
        "🔍 [PARENT-WINDOW] Step 1: Verifying storage permissions...",
      );
      const finalBucketName = bucketName || "files";
      console.log("🔍 [PARENT-WINDOW] Using bucket:", finalBucketName);

      console.log(
        "🔍 [PARENT-WINDOW] Step 2: Creating standardized filename...",
      );
      const standardizedFilename =
        ExternalRecordingService.createStandardizedFilename(data);
      console.log(
        "🔍 [PARENT-WINDOW] Standardized filename:",
        standardizedFilename,
      );

      console.log("🔍 [PARENT-WINDOW] Step 3: Uploading FINAL audio file...");
      const uploadResult = await uploadAudioFile(
        data.audioBlob,
        standardizedFilename,
        finalBucketName,
        supabaseUrl,
        {
          patientId: data.patientId,
          patientName: data.patientName,
          ehrId: data.ehrId || data.ehr_id || data.EHR_ID,
          recordingDate: data.timestamp,
          duration: data.duration,
          source: "external_recording_window_final",
          contentType: data.audioBlob.type,
          originalFilename: standardizedFilename,
          verified_upload: true,
          isFinalRecording: true, // Mark as final recording
          totalBackups: data.totalBackups || 0,
          scheduledDate:
            data?.patientScheduledDate || data?.scheduledDate || state?.date_,
        },
        data?.patientScheduledDate || data?.scheduledDate || state?.date_,
      );

      if (!uploadResult.success) {
        throw new Error(
          `Upload verification failed: ${uploadResult.error || "Unknown error"}`,
        );
      }

      console.log("✅ [FINAL-UPLOAD] Upload successful:", {
        url: uploadResult.url,
        userSchema: uploadResult.verification?.userSchema,
        schemaBasedPath: uploadResult.verification?.schemaBasedPath,
      });

      console.log(
        "✅ [PARENT-WINDOW] Upload successful, URL:",
        uploadResult.url,
      );

      // Skip creating backup for final recording - we already have the complete audio
      console.log(
        "🔍 [PARENT-WINDOW] Step 3.5: Skipping backup creation for final recording",
      );

      console.log("🔍 [PARENT-WINDOW] Step 4: Updating database entry...");
      const updateResult = await updateDatabaseEntry(
        tableForPatientId(data.patientId),
        data.patientId,
        uploadResult.url,
        data.duration,
        {
          audio_source: "external_recording_window_final",
          recording_timestamp: data.timestamp,
          verified_update: true,
          is_final_recording: true,
          total_backups: data.totalBackups || 0,
        },
        chartRowSchema,
      );

      if (!updateResult.success) {
        throw new Error(
          `Database update verification failed: ${
            updateResult.error || "Unknown error"
          }`,
        );
      }

      console.log("✅ [PARENT-WINDOW] Database update successful");
      console.log(
        "📊 [PARENT-WINDOW] Final audio count:",
        updateResult.verification?.finalAudioCount,
      );

      const audioData = {
        name: `Recording ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString(
          [],
          { hour: "2-digit", minute: "2-digit" },
        )}`,
        description: "External recording - Final",
        url: uploadResult.url,
        duration: data.duration,
        source: "external_recording_window_final",
        timestamp: data.timestamp,
        uploaded_at: new Date().toISOString(),
        verified: true,
        type: "audio",
        patient_id: data.patientId,
        recording_date: new Date().toISOString(),
        file_size: null,
        content_type: data.audioBlob?.type || "audio/webm",
        displayName: `Recording ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString(
          [],
          { hour: "2-digit", minute: "2-digit" },
        )}`,
        liveTranscription: data.liveTranscription || false,
        liveFactExtraction: data.liveFactExtraction || false,
        isFinalRecording: true,
        totalBackups: data.totalBackups || 0,
      };

      // uploadAudioFile already verified the object through a signed URL — the
      // buckets are private, so re-checking uploadResult.url (a public-shaped
      // pointer) would only ever answer 400. Reuse that result.
      console.log("🔍 [PARENT-WINDOW] Step 6: Verifying file accessibility...");
      if (uploadResult.verification?.accessible) {
        console.log("✅ [PARENT-WINDOW] File is accessible");
      } else {
        console.warn(
          "⚠️ [PARENT-WINDOW] File accessibility check failed for",
          uploadResult.path,
        );
      }

      // Clean up temporary backups after successful final recording
      console.log("🧹 [PARENT-WINDOW] Cleaning up temporary backups...");
      try {
        const cleanupResult =
          await ExternalRecordingService.cleanupTemporaryBackups(
            data.patientId,
            uploadResult.url,
            { userSchema: uploadResult.verification?.userSchema },
          );

        // Also clear the recovery backup since recording was successful
        ExternalRecordingService.clearRecoveryBackup(data.patientId);

        if (cleanupResult.success) {
          console.log(
            "🧹 [PARENT-WINDOW] Cleanup completed:",
            cleanupResult.message,
          );
        } else {
          console.warn(
            "🧹 [PARENT-WINDOW] Cleanup warning:",
            cleanupResult.error,
          );
        }
      } catch (cleanupError) {
        console.warn(
          "⚠️ [PARENT-WINDOW] Backup cleanup error (non-critical):",
          cleanupError.message,
        );
      }

      if (onAudioSaved) {
        onAudioSaved({
          success: true,
          audioUrl: uploadResult.url,
          audioData: audioData,
          verification: updateResult.verification,
          liveTranscriptText: data.liveTranscriptText || "",
          generateAiRecap: data.generateAiRecap || false,
          duration: data.duration,
          timestamp: data.timestamp,
          sessionId: data.sessionId || null,
          patientId: data.patientId || null,
        });
      }

      ExternalRecordingService.triggerRefresh(data.patientId, uploadResult.url);

      console.log(
        "🎉 [PARENT-WINDOW] Complete FINAL save flow successful for patient ID:",
        data.patientId,
      );
      console.log("🔗 [PARENT-WINDOW] Audio URL:", uploadResult.url);
      console.log(
        "📊 [PARENT-WINDOW] Verification details:",
        updateResult.verification,
      );
      console.log(
        "🎤 [PARENT-WINDOW] ✅ FINAL AUDIO PROCESSING COMPLETE - SUCCESS!",
      );

      return {
        success: true,
        audioUrl: uploadResult.url,
        audioData,
        verification: updateResult.verification,
      };
    } catch (error) {
      console.error(
        "❌ [PARENT-WINDOW] Error in FINAL audio save flow:",
        error,
      );

      // Attempt fallback to last successful backup
      const lastBackup = ExternalRecordingService.getLastBackupForPatient(
        data?.patientId,
      );
      if (lastBackup) {
        console.warn(
          "⚠️ [PARENT-WINDOW] Main save failed — falling back to last backup:",
          lastBackup,
        );
        try {
          const fallbackUpdateResult = await updateDatabaseEntry(
            tableForPatientId(data.patientId),
            data.patientId,
            lastBackup.url,
            data.duration,
            {
              audio_source: "backup_fallback",
              recording_timestamp: data.timestamp,
              verified_update: true,
              is_backup_fallback: true,
            },
            chartRowSchema,
          );

          if (fallbackUpdateResult.success) {
            console.log(
              "✅ [PARENT-WINDOW] Backup fallback saved successfully",
            );

            const fallbackAudioData = {
              name: `Recording ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} (recovered)`,
              url: lastBackup.url,
              duration: data.duration,
              source: "backup_fallback",
              uploaded_at: new Date().toISOString(),
              type: "audio",
              patient_id: data.patientId,
              isFallback: true,
            };

            if (onAudioSaved) {
              onAudioSaved({
                success: true,
                audioUrl: lastBackup.url,
                audioData: fallbackAudioData,
                verification: fallbackUpdateResult.verification,
                isFallback: true,
                originalError: error.message,
              });
            }

            ExternalRecordingService.triggerRefresh(
              data.patientId,
              lastBackup.url,
            );

            return {
              success: true,
              audioUrl: lastBackup.url,
              audioData: fallbackAudioData,
              isFallback: true,
              originalError: error.message,
            };
          }
        } catch (fallbackError) {
          console.error(
            "❌ [PARENT-WINDOW] Backup fallback also failed:",
            fallbackError,
          );
        }
      } else {
        console.warn("⚠️ [PARENT-WINDOW] No backup available for fallback");
      }

      const errorDetails = {
        message: error.message,
        patientId: data?.patientId,
        timestamp: new Date().toISOString(),
        step: error.step || "unknown",
        bucketName: bucketName || "files",
        tableName: tableName || "notes-c",
      };

      console.error("❌ [PARENT-WINDOW] Error details:", errorDetails);

      if (onAudioSaved) {
        onAudioSaved({
          success: false,
          error: error.message,
          details: errorDetails,
        });
      }

      return {
        success: false,
        error: error.message,
        details: errorDetails,
      };
    }
  },

    /**
   * Update database entry when audio was already uploaded directly by external window
   * This is called when AUDIO_SAVED_SUCCESS message is received from external window
   */
  updateRecordingDatabaseEntry: async ({
    patientId,
    audioData,
    supabase,
    schemaName,
    tableName: tbl,
    bucketName,
    supabaseUrl,
  }) => {
    console.log("🎤 [DIRECT-UPLOAD] Updating database entry:", {
      patientId,
      audioUrl: audioData?.url,
      schemaName,
      tableName: tbl,
    });

    try {
      const table = tbl || "audio_notes";
      const url = audioData.url;
      const duration = audioData.duration || 0;

      // Build update metadata
      const updateMetadata = {
        audio_source: "direct_upload_external_window",
        recording_timestamp: audioData.timestamp || new Date().toISOString(),
        verified_update: true,
        is_direct_upload: true,
        source: "external_recording_window_direct",
      };

      // Call updateDatabaseEntry from databaseUtils. `schemaName` is the chart
      // row's schema, which is NOT the viewer's for a cross-schema chart.
      const updateResult = await updateDatabaseEntry(
        table,
        patientId,
        url,
        duration,
        updateMetadata,
        schemaName
      );

      if (updateResult.success) {
        console.log("🎤 [DIRECT-UPLOAD] Database updated successfully");
        
        // Trigger refresh
        ExternalRecordingService.triggerRefresh(patientId, url);
        
        return {
          success: true,
          audioUrl: url,
          verification: updateResult.verification,
        };
      } else {
        throw new Error(updateResult.error || "Database update failed");
      }
    } catch (error) {
      console.error("🎤 [DIRECT-UPLOAD] Database update error:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  /**
   * Trigger UI refresh after successful recording
   */
  triggerRefresh: (patientId, audioUrl) => {
    console.log("🔍 [PARENT-WINDOW] Step 8: Triggering enhanced refresh...");
    const timestamp = new Date().toISOString();

    localStorage.setItem("patientUpdated", timestamp);
    localStorage.setItem(`patientUpdated_${patientId}`, timestamp);

    if (window.location.pathname.includes(`/patient/${patientId}`)) {
      console.log("🔄 [PARENT-WINDOW] Reloading patient page immediately...");
      window.location.reload();
      return;
    }

    window.dispatchEvent(
      new CustomEvent("patientDataRefresh", {
        detail: {
          patientId: patientId,
          timestamp: timestamp,
          type: "enhanced_refresh",
          audioUrl: audioUrl,
          verified: true,
          forceReload: true,
        },
      }),
    );

    window.dispatchEvent(
      new CustomEvent("audioRecordingComplete", {
        detail: {
          patientId: patientId,
          audioUrl: audioUrl,
          timestamp: timestamp,
          verified: true,
        },
      }),
    );
  },

  /**
   * Process periodic backup during recording (TEMPORARY ONLY - not saved permanently)
   */
  processPeriodicBackup: async (data, config) => {
    const { bucketName, supabaseUrl, state } = config;

    console.log("💾 [PARENT-WINDOW] Processing periodic backup (TEMPORARY):", {
      type: data?.type,
      backupNumber: data?.backupNumber,
      duration: data?.duration,
      patientId: data?.patientId,
      hasAudioBlob: !!data?.audioBlob,
      sessionId: data?.sessionId,
      isTemporary: data?.isTemporary,
    });

    if (data.type !== "AUDIO_RECORDING_BACKUP") {
      console.log("💾 [PARENT-WINDOW] Ignored message type:", data?.type);
      return { success: false, error: "Invalid message type" };
    }

    const now = new Date();
    const backupNumber = data.backupNumber || 0;
    const periodicLocalKey = `backup_${data.patientId}_periodic_${sanitizeRollingSessionSlug(data.sessionId)}`;

    let backupResult = null;

    // Popup already uploaded to backup-audio (avoids postMessage blob clone limits on long recordings)
    if (data.backupPreUploaded && data.backupUrl) {
      console.log(
        "💾 [PARENT-WINDOW] Using pre-uploaded periodic backup from popup:",
        data.backupUrl,
      );
      backupResult = {
        success: true,
        url: data.backupUrl,
        path: data.backupPath || null,
      };
    } else {
      // Validate audio data for parent-side upload path
      if (!data.audioBlob || data.audioBlob.size === 0) {
        console.warn("💾 [PARENT-WINDOW] Skipping backup - no audio data");
        return { success: false, error: "No audio data to backup" };
      }

      try {
        console.log(
          "💾 [PARENT-WINDOW] Starting TEMPORARY periodic backup processing...",
        );

        const timestamp = now.toISOString().replace(/[:.]/g, "-");
        const filename = `temp_backup_${backupNumber}_${timestamp}.webm`;

        console.log("💾 [PARENT-WINDOW] TEMPORARY backup filename:", filename);

        let backupChunks;
        try {
          const arrayBuffer = await data.audioBlob.arrayBuffer();
          backupChunks = [new Uint8Array(arrayBuffer)];
        } catch (blobError) {
          throw new Error(`Failed to process audio blob: ${blobError.message}`);
        }

        if (!backupChunks || backupChunks.length === 0) {
          throw new Error("No backup chunks generated from audio blob");
        }

        backupResult = await createBackup(
          backupChunks,
          data.audioBlob.type || "audio/webm",
          filename,
          null,
          {
            patientId: data.patientId,
            patientName: data.patientName,
            ehrId: data.ehrId || data.ehr_id || data.EHR_ID,
            scheduledDate:
              data?.patientScheduledDate || data?.scheduledDate || state?.date_,
            recordingDate: data.timestamp,
            duration: data.duration,
            source: "periodic_backup_during_recording",
            contentType: data.audioBlob.type,
            originalFilename: filename,
            isPeriodicBackup: true,
            backupNumber: backupNumber,
            backupTimestamp: now.toISOString(),
            isTemporary: true,
            sessionId: data.sessionId,
            session_id: data.sessionId || null,
            audioNumber: data.audioNumber ?? data.audio_number,
            audio_number: data.audioNumber ?? data.audio_number,
          },
        );
      } catch (uploadPathError) {
        console.error(
          "❌ [PARENT-WINDOW] Error in periodic backup upload path:",
          uploadPathError,
        );
        return {
          success: false,
          error: uploadPathError.message,
          backupNumber: backupNumber,
          timestamp: now.toISOString(),
        };
      }
    }

    try {
      if (backupResult.success) {
        console.log(
          "💾 [PARENT-WINDOW] TEMPORARY periodic backup created successfully:",
          {
            backupUrl: backupResult.url,
            backupPath: backupResult.path,
            backupNumber: backupNumber,
          },
        );

        // Also persist recovery backup for unexpected termination scenarios
        try {
          ExternalRecordingService.persistRecoveryBackup(data.patientId, {
            url: backupResult.url,
            path: backupResult.path,
            timestamp: now.toISOString(),
            backupNumber: backupNumber,
            patientId: data.patientId,
            sessionId: data.sessionId,
            duration: data.duration,
          });
        } catch (persistError) {
          console.warn(
            "💾 [RECOVERY] Failed to persist recovery backup:",
            persistError.message,
          );
        }

        // Store backup reference for potential cleanup with error handling
        try {
          const backupData = {
            url: backupResult.url,
            path: backupResult.path,
            timestamp: now.toISOString(),
            backupNumber: backupNumber,
            patientId: data.patientId,
            sessionId: data.sessionId,
            duration: data.duration,
          };

          // Check if localStorage is available and working
          if (typeof Storage !== "undefined") {
            purgeStalePeriodicBackupLocalRefs(data.patientId, periodicLocalKey);
            localStorage.setItem(
              periodicLocalKey,
              JSON.stringify(backupData),
            );
            console.log(
              "💾 [PARENT-WINDOW] Periodic backup reference stored (rolling key):",
              periodicLocalKey,
            );
          } else {
            console.warn(
              "💾 [PARENT-WINDOW] localStorage not available, skipping reference storage",
            );
          }
        } catch (storageError) {
          console.warn(
            "💾 [PARENT-WINDOW] Failed to store backup reference (non-critical):",
            storageError.message,
          );
        }

        try {
          await prunePeriodicAudioBackupsDuringRecording(data.patientId, null);
        } catch (pruneError) {
          console.warn(
            "💾 [PARENT-WINDOW] Periodic backup prune (non-critical):",
            pruneError?.message || pruneError,
          );
        }

        return {
          success: true,
          backupUrl: backupResult.url,
          backupPath: backupResult.path,
          backupNumber: backupNumber,
          timestamp: now.toISOString(),
          isTemporary: true,
        };
      }

      throw new Error(
        `Backup creation failed: ${backupResult.error || "Unknown error"}`,
      );
    } catch (error) {
      console.error(
        "❌ [PARENT-WINDOW] Error in periodic backup processing:",
        error,
      );

      return {
        success: false,
        error: error.message,
        backupNumber: data?.backupNumber || 0,
        timestamp: new Date().toISOString(),
      };
    }
  },

  /**
   * Get the most recent successful backup for a patient from localStorage
   */
  getLastBackupForPatient: (patientId) => {
    let lastBackup = null;
    let highestBackupNumber = -1;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`backup_${patientId}_`)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.url && data.backupNumber > highestBackupNumber) {
            highestBackupNumber = data.backupNumber;
            lastBackup = data;
          }
        } catch (e) {
          // ignore malformed entries
        }
      }
    }

    return lastBackup;
  },

  /**
   * Get ALL backups for a patient (for recovery after unexpected termination)
   * This includes persisted backup references that survive cleanup
   */
  getAllBackupsForPatient: (patientId) => {
    const backups = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`backup_${patientId}_`)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.url) {
            backups.push(data);
          }
        } catch (e) {
          // ignore malformed entries
        }
      }
    }

    // Sort by backupNumber descending (most recent first)
    backups.sort((a, b) => (b.backupNumber || 0) - (a.backupNumber || 0));

    return backups;
  },

  /**
   * Persist a recovery backup that survives cleanup
   * This is used to keep backup references for recovery after unexpected termination
   */
  persistRecoveryBackup: (patientId, backupData) => {
    try {
      const recoveryKey = `recovery_backup_${patientId}`;
      const data = {
        ...backupData,
        persistedAt: new Date().toISOString(),
      };
      localStorage.setItem(recoveryKey, JSON.stringify(data));
      console.log("💾 [RECOVERY] Persisted recovery backup:", recoveryKey);
      return { success: true };
    } catch (error) {
      console.error("💾 [RECOVERY] Failed to persist recovery backup:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get persisted recovery backup for a patient
   */
  getRecoveryBackup: (patientId) => {
    try {
      const recoveryKey = `recovery_backup_${patientId}`;
      const data = localStorage.getItem(recoveryKey);
      if (data) {
        return JSON.parse(data);
      }
      return null;
    } catch (error) {
      console.error("💾 [RECOVERY] Failed to get recovery backup:", error);
      return null;
    }
  },

  /**
   * Clear persisted recovery backup after successful recording
   */
  clearRecoveryBackup: (patientId) => {
    try {
      const recoveryKey = `recovery_backup_${patientId}`;
      localStorage.removeItem(recoveryKey);
      console.log("💾 [RECOVERY] Cleared recovery backup:", recoveryKey);
      return { success: true };
    } catch (error) {
      console.error("💾 [RECOVERY] Failed to clear recovery backup:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * After a successful save: remove old periodic backup objects from storage per
   * schema_settings.audio_periodic_backup.keep_count, keeping newest-first until enough
   * playable backups are retained. Clears localStorage refs for the session.
   */
  cleanupTemporaryBackups: async (
    patientId,
    preservedRecordingUrl,
    options = {},
  ) => {
    console.log(
      "🧹 [PARENT-WINDOW] Cleaning up temporary backups for patient:",
      patientId,
    );

    try {
      const bucket = backupBucketName || "backup-audio";
      let userSchema = options.userSchema;
      if (!userSchema) {
        try {
          userSchema = await getUserSchema();
        } catch {
          userSchema = "public";
        }
      }
      const { keepCount: maxKeep } =
        await fetchAudioPeriodicBackupSettingsFromDb(userSchema);

      const backupKeys = [];
      const backupEntries = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`backup_${patientId}_`)) {
          backupKeys.push(key);
          try {
            const raw = localStorage.getItem(key);
            const data = raw ? JSON.parse(raw) : null;
            if (data && data.url) backupEntries.push(data);
          } catch {
            // ignore malformed
          }
        }
      }

      console.log("🧹 [PARENT-WINDOW] Backup retention:", {
        maxKeep,
        userSchema,
        localRefs: backupKeys.length,
      });

      const pathsToRemove = await selectBackupPathsToRemove(
        backupEntries,
        maxKeep,
        preservedRecordingUrl,
        bucket,
        { trustUploadedRefs: true },
      );
      const allPathsToRemove = expandPathsWithMetadata(pathsToRemove);

      if (allPathsToRemove.length > 0) {
        const supabase = await getSupabase();
        const { error: removeError } = await supabase.storage
          .from(bucket)
          .remove(allPathsToRemove);
        if (removeError) {
          console.warn(
            "🧹 [PARENT-WINDOW] Storage remove warning:",
            removeError.message,
          );
        } else {
          console.log(
            "🧹 [PARENT-WINDOW] Removed backup objects from storage:",
            allPathsToRemove.length,
          );
        }
      }

      backupKeys.forEach((key) => {
        localStorage.removeItem(key);
        console.log("🧹 [PARENT-WINDOW] Removed backup reference:", key);
      });

      return {
        success: true,
        cleanedUpCount: backupKeys.length,
        removedFromStorage: allPathsToRemove.length,
        message: `Removed ${allPathsToRemove.length} backup file(s); cleared ${backupKeys.length} local ref(s)`,
      };
    } catch (error) {
      console.error("❌ [PARENT-WINDOW] Error during backup cleanup:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  },

  /**
   * Create external window HTML with external recording service
   */
  generateExternalWindowHTML: (options) => {
    const {
      patientId,
      patientName,
      scheduledDate,
      liveTranscription,
      liveFactExtraction,
    } = options;

    // This would contain the external window HTML generation logic
    // Similar to the generateExternalHTML function from the original component
    // For now, we'll return a placeholder structure
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>External Recording Window - ${patientName || "Patient"}</title>
          <!-- Styles would be included here -->
      </head>
      <body>
          <!-- External window content would be generated here -->
      </body>
      </html>
    `;
  },
};

export default ExternalRecordingService;
